import React, { useEffect, useState, useRef, useContext } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { FaHeart, FaChevronLeft, FaChevronRight } from "react-icons/fa";
import "../product/ProductCard.css";
import { AuthContext } from "../../routes/AuthContext";
import { toast } from "react-toastify";
import { addToWishlist, removeFromWishlist } from "../wishlist/Wishlist";
import { addToCart } from "../cart/Cart";

const OfferCard = () => {
  const [offerProducts, setOfferProducts] = useState([]);
  const [wishlistIds, setWishlistIds] = useState([]);
  const [addingId, setAddingId] = useState(null);
  const [cartQtyMap, setCartQtyMap] = useState({});
  const [loading, setLoading] = useState(true);

  const [scrollState, setScrollState] = useState({
    showLeftArrow: false,
    showRightArrow: true,
  });
  const [hasScrolled, setHasScrolled] = useState(false); // NEW: Track if user has scrolled

  const sliderRef = useRef(null);
  const navigate = useNavigate();
  const BASE_URL = process.env.REACT_APP_API_BASE_URL;

  const { getCustomerData, isAuthenticated } = useContext(AuthContext);
  const customer = getCustomerData();
  const customer_id = customer?.customer_id;

  /* =========================
     FETCH OFFER ITEMS
  ========================== */
  useEffect(() => {
    const fetchOffers = async () => {
      try {
        const res = await axios.post(`${BASE_URL}offer_items`);

        if (res.data.api_success) {
          const products = res.data.data.flatMap((offer) =>
            offer.products.map((p) => ({
              id: p.product_id,
              product_name: p.product_name,
              offer_price: p.offer_price,
              cost_price: p.cost_price,
              offer_percentage: p.offer_percentage,
              images: p.images,
            }))
          );

          setOfferProducts(products);
        }
      } catch (error) {
        console.error("Offer API error:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchOffers();
  }, [BASE_URL]);

  /* =========================
     FETCH WISHLIST IDS
  ========================== */
  useEffect(() => {
    if (!customer_id) return;

    const fetchWishlistIds = async () => {
      try {
        const formData = new FormData();
        formData.append("customer_id", customer_id);

        const res = await axios.post(
          `${BASE_URL}customer_wish_list_details`,
          formData
        );

        if (res.data.api_success) {
          const ids = res.data.wishlist_items.map(
            (item) => item.product_id
          );
          setWishlistIds(ids);
        }
      } catch (error) {
        console.error("Wishlist fetch error", error);
      }
    };

    fetchWishlistIds();
  }, [customer_id, BASE_URL]);

  /* =========================
     ❤️ TOGGLE WISHLIST
  ========================== */
  const toggleWishlist = async (product_id) => {
    if (!isAuthenticated || !customer_id) {
      toast.warning("Please login to use wishlist");
      return;
    }

    const isWishlisted = wishlistIds.includes(product_id);

    if (isWishlisted) {
      await removeFromWishlist(customer_id, product_id);
      setWishlistIds((prev) => prev.filter((id) => id !== product_id));
      toast.info("Removed from wishlist");
    } else {
      await addToWishlist(customer_id, product_id);
      setWishlistIds((prev) => [...prev, product_id]);
      toast.success("Added to wishlist");
    }
  };

  /* =========================
     ADD TO CART (SAME AS TOP SELLING)
  ========================== */
  const handleAddToCart = async (e, product_id) => {
    e.stopPropagation();

    if (!isAuthenticated || !customer_id) {
      toast.warning("Please login to add items to cart");
      return;
    }

    if (addingId === product_id) return;

    try {
      setAddingId(product_id);

      const currentQty = cartQtyMap[product_id] || 0;
      const newQty = currentQty + 1;

      const res = await addToCart(customer_id, product_id, newQty);

      if (res.api_success) {
        setCartQtyMap((prev) => ({
          ...prev,
          [product_id]: newQty,
        }));

        toast.success(
          currentQty === 0 ? "Added to cart" : "Quantity increased"
        );
      } else {
        toast.error("Failed to add to cart");
      }
    } catch (error) {
      toast.error("Something went wrong");
    } finally {
      setTimeout(() => setAddingId(null), 400);
    }
  };

  /* =========================
     SCROLL HELPERS
  ========================== */
  const checkScrollPosition = () => {
    const slider = sliderRef.current;
    if (!slider) return;

    const isAtStart = slider.scrollLeft <= 10;
    const isAtEnd =
      slider.scrollLeft + slider.clientWidth >=
      slider.scrollWidth - 10;

    // ✅ Mark that user has scrolled
    if (slider.scrollLeft > 0) {
      setHasScrolled(true);
    }

    setScrollState({
      showLeftArrow: !isAtStart,
      showRightArrow: !isAtEnd,
    });
  };

  const scroll = (direction) => {
    const slider = sliderRef.current;
    if (!slider) return;

    const scrollAmount = 400;
    slider.scrollTo({
      left:
        direction === "left"
          ? slider.scrollLeft - scrollAmount
          : slider.scrollLeft + scrollAmount,
      behavior: "smooth",
    });
  };

  useEffect(() => {
    const slider = sliderRef.current;
    if (!slider) return;

    checkScrollPosition();
    slider.addEventListener("scroll", checkScrollPosition);

    return () =>
      slider.removeEventListener("scroll", checkScrollPosition);
  }, [offerProducts]);

  if (loading) {
    return <p style={{ textAlign: "center" }}>Loading offers...</p>;
  }

  if (offerProducts.length === 0) return null;

  return (
    <div className="product-grid-container">
      {/* HEADER */}
      <div className="category-header">
        <h2 className="category-heading">Today's Offers</h2>
      </div>

      <div className="slider-wrapper">
        {/* Left Arrow - Only show if user has scrolled */}
        {hasScrolled && scrollState.showLeftArrow && (
          <button
            className="slider-arrow slider-arrow-left"
            onClick={() => scroll("left")}
          >
            <FaChevronLeft />
          </button>
        )}

        {/* OFFER ROW */}
        <div className="product-grid offer-row" ref={sliderRef}>
          {offerProducts.map((item) => {
            const isWishlisted = wishlistIds.includes(item.id);

            return (
              <div
                key={item.id}
                className="product-card"
                onClick={() =>
                  navigate(`/product-details/${item.id}`)
                }
              >
                <div className="product-image-wrapper">
                  <button
                    className={`favorite-btn ${
                      isWishlisted ? "active" : ""
                    }`}
                    onClick={(e) => {
                      e.stopPropagation();
                      toggleWishlist(item.id);
                    }}
                  >
                    <FaHeart />
                  </button>

                  <img
                    src={item.images?.[0]}
                    alt={item.product_name}
                    className="product-image"
                  />
                </div>

                <div className="product-info">
                  {/*OFF BADGE IN TOP RIGHT */}
                  {item.offer_percentage && (
                    <span className="offer-badge">
                      {item.offer_percentage}% OFF
                    </span>
                  )}

                  <h3 className="product-name">
                    {item.product_name}
                  </h3>

                  <div className="product-footer">
                    <div className="product-price-wrapper">
                      {item.cost_price && (
                        <span className="product-cost-price">
                          ₹{item.cost_price}
                        </span>
                      )}
                      <span className="product-selling-price">
                        ₹{item.offer_price}
                      </span>
                    </div>

                    <button
                      className="add-btn"
                      onClick={(e) =>
                        handleAddToCart(e, item.id)
                      }
                      disabled={addingId === item.id}
                    >
                      {addingId === item.id ? "..." : "+"}
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* Right Arrow - Only show if user has scrolled */}
        {hasScrolled && scrollState.showRightArrow && (
          <button
            className="slider-arrow slider-arrow-right"
            onClick={() => scroll("right")}
          >
            <FaChevronRight />
          </button>
        )}
      </div>
    </div>
  );
};

export default OfferCard;